/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    createPersistentItem,
    createShortUid,
    globalRef,
    logDebug,
    logWarn,
} from '@amazon/vinyl-util'
import { type DeviceInfo } from '@amzn/vinyl-amzn'
import { deviceConfigs, getDeviceConfig } from '@/device/deviceConfig'
import { localSignInPersistence } from '@/core/signInPersistence'
import { desktopShellRef } from '@/device/desktopShell'
import { ctx } from '@/util/context'
import { type ObjectSchema, object, string } from '@amazon/vinyl-validation'

const { target, errorHandler } = ctx('device')

const deviceInfoValidator: ObjectSchema<DeviceInfo> = object({
    deviceId: string(),
    deviceName: string(),
    deviceType: string(),
})

const localDeviceInfo = createPersistentItem<DeviceInfo | null>({
    defaultValue: null,
    key: 'deviceInfo',
    validator: deviceInfoValidator.orNull().assert,
})

const loadingDeviceInfoRef = globalRef<{ promise: Promise<DeviceInfo> | null }>(() => ({
    promise: null,
}))

/**
 * Resolves this browser's device identity, reading or creating it on the first call. A request
 * awaits it, so it can never go out without one, even from a second copy of this module (a dev page that lazily loads modules changed since it booted).
 */
export function loadDeviceInfo(): Promise<DeviceInfo> {
    const loading = loadingDeviceInfoRef.value
    loading.promise ??= readOrCreateDeviceInfo().then((dI) => {
        logDebug(target, 'loadDeviceInfo', dI)
        return dI
    })
    return loading.promise
}

async function readOrCreateDeviceInfo(): Promise<DeviceInfo> {
    // The desktop shell owns the device its tokens are registered to.
    if (getDeviceConfig().authType === 'desktop') return desktopShellRef.value.device()
    const dI = await localDeviceInfo.get()
    if (dI && dI.deviceType === getDeviceConfig().deviceTypeId) return dI
    if (dI && isWebPlayer(dI.deviceType) !== isWebPlayer(getDeviceConfig().deviceTypeId)) {
        // The device type has changed and has not changed between web player and web player mobile, sign out.
        logWarn(target, 'deviceType changed from persistence')
        localSignInPersistence.remove().catch(errorHandler)
    }
    const created = createDeviceInfo()
    // Persisting is best-effort: a write that throws (private-mode Safari, a full quota) must
    // degrade to an identity that lasts this session rather than take the boot down.
    await localDeviceInfo.set(created).catch(errorHandler)
    return created
}

/**
 * Returns true if device type is web player or web player mobile.
 */
function isWebPlayer(deviceTypeId: string): boolean {
    return (
        deviceTypeId === deviceConfigs.WebPlayer.deviceTypeId ||
        deviceTypeId === deviceConfigs.WebPlayerMobile.deviceTypeId
    )
}

export function createDeviceInfo(): DeviceInfo {
    const deviceId = createShortUid()
    return {
        deviceId,
        deviceName: `VinylAmznTestDevice_${deviceId}`,
        deviceType: getDeviceConfig().deviceTypeId,
    }
}
